import { ApiClient } from './ApiClient';
import type { PaymentTrackingQuery, PaymentTrackingDto, ApiResponse } from './PaymentTrackingTypes';

export interface StudentPrintExportRequest {
    fromDate?: string | null;
    toDate?: string | null;
    /** @deprecated Dùng trainingTypeIds */
    trainingTypeId?: number | null;
    trainingTypeIds?: number[] | null;
    /** ClassTypeEnum: 1 = Văn hóa, 2 = Nghề nghiệp */
    classType?: number | null;
    decisionNumber?: string | null;
    signer?: string | null;
    signingDate?: string | null;
}

export interface ClassPromoteNormalFilter {
    academicYearIds?: number[] | null;
    trainingTypes?: number[] | null;
    majors?: number[] | null;
}

export interface ClassPromoteNormalPreviewItem {
    classId: number;
    oldClassName: string;
    oldClassCode: string;
    newClassName?: string | null;
    newClassCode?: string | null;
    academicYearId: number;
    studyingStudentCount: number;
    action: "promote" | "graduate" | "skip" | string;
    actionLabel: string;
}

export interface ClassPromoteNormalConfirmResult {
    promotedClassCount: number;
    graduatedClassCount: number;
    createdClassCount: number;
    movedStudentCount: number;
    graduatedStudentCount: number;
}

export class ApiClientExtended extends ApiClient {
    /**
     * Download file dạng blob từ API endpoint
     */
    async downloadBlob(url: string, method: string = "GET", data?: any): Promise<Blob> {
        const response = await this.instance.request({
            method,
            url: this.baseUrl + url,
            data: data ? JSON.stringify(data) : undefined,
            headers: data ? { "Content-Type": "application/json" } : undefined,
            responseType: "blob",
        });
        return new Blob([response.data]);
    }

    async downloadBlobForm(url: string, formData: FormData): Promise<Blob> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + url,
            data: formData,
            responseType: "blob",
        });
        return new Blob([response.data]);
    }

    /**
     * Xuất Excel danh sách miễn giảm học phí (trả về Blob)
     */
    async tuitionExemptionExportExcel(filter: any): Promise<Blob> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/export-excel",
            data: filter,
            headers: { "Content-Type": "application/json" },
            responseType: "blob",
        });
        return new Blob([response.data]);
    }

    /**
     * Cập nhật hàng loạt thông tin ngân hàng cho miễn giảm học phí
     */
    async tuitionExemptionBulkUpdateBank(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/bulk-update-bank",
            data: formData,
        });
        return response.data;
    }

    /**
     * Lấy thống kê tiến trình nộp học phí
     */
    /** Đợt xác nhận số tiền chi trả (phụ huynh) — admin */
    async tuitionExemptionParentConfirmBatchList(): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches",
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmBatchSave(body: Record<string, unknown>): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmBatchDelete(id: number): Promise<any> {
        const response = await this.instance.request({
            method: "DELETE",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches/" + id,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmResponseList(batchId: number): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/tuition-exemption/parent-confirm/batches/" + batchId + "/responses",
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    /** Công khai — không cần token đăng nhập */
    async tuitionExemptionParentConfirmPublicGet(token: string): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/public/tuition-exemption/parent-confirm/" + token,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async tuitionExemptionParentConfirmPublicSubmit(token: string, body: { studentCode: string; fullName: string; amountConfirmed: number }): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/public/tuition-exemption/parent-confirm/" + token + "/submit",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    /**
     * Thống kê miễn giảm theo phường — thêm lọc nhiều xã/phường (wardIds).
     */
    async tuitionExemptionStatsByWardFiltered(
        exemptionYear: string | undefined,
        semester: number | undefined,
        provinceId: number | undefined,
        submissionMethod: number | undefined,
        status: number | undefined,
        wardIds?: number[],
        paymentPayout?: number
    ): Promise<any> {
        let url_ = this.baseUrl + "/api/v1/tuition-exemption/stats-by-ward?";
        if (exemptionYear !== undefined)
            url_ += "exemptionYear=" + encodeURIComponent("" + exemptionYear) + "&";
        if (semester !== undefined)
            url_ += "semester=" + encodeURIComponent("" + semester) + "&";
        if (provinceId !== undefined)
            url_ += "provinceId=" + encodeURIComponent("" + provinceId) + "&";
        if (submissionMethod !== undefined)
            url_ += "submissionMethod=" + encodeURIComponent("" + submissionMethod) + "&";
        if (status !== undefined)
            url_ += "status=" + encodeURIComponent("" + status) + "&";
        if (paymentPayout !== undefined)
            url_ += "paymentPayout=" + encodeURIComponent("" + paymentPayout) + "&";
        if (wardIds != null && wardIds.length > 0) {
            for (const w of wardIds) {
                url_ += "wardIds=" + encodeURIComponent("" + w) + "&";
            }
        }
        url_ = url_.replace(/[?&]$/, "");
        const response = await this.instance.request({
            method: "GET",
            url: url_,
            headers: { Accept: "text/plain" },
        });
        return this.processTuitionExemptionStatsByWard(response);
    }

    async receiptGetPaymentTracking(query: PaymentTrackingQuery): Promise<ApiResponse<PaymentTrackingDto>> {
        const url = this.baseUrl + "/api/v1/receipt/payment-tracking";
        
        const options = {
            method: "POST",
            url: url,
            headers: {
                "Content-Type": "application/json",
                "Accept": "text/plain"
            },
            data: JSON.stringify(query)
        };

        try {
            const response = await this.instance.request(options);
            return response.data;
        } catch (error) {
            console.error("Error calling receiptGetPaymentTracking:", error);
            throw error;
        }
    }

    // ─── Student Print ────────────────────────────────────────────────────────

    /** Xuất hồ sơ Thôi học (ZIP: Đơn + QĐ + DS Excel) */
    async studentPrintExportDropout(request: StudentPrintExportRequest): Promise<Blob> {
        return this.downloadBlob("/api/v1/student-print/export-dropout", "POST", request);
    }

    /** Xuất hồ sơ Bảo lưu (ZIP: Đơn + QĐ + DS Excel) */
    async studentPrintExportDeferred(request: StudentPrintExportRequest): Promise<Blob> {
        return this.downloadBlob("/api/v1/student-print/export-deferred", "POST", request);
    }

    /** Xuất hồ sơ Quay lại (ZIP: Đơn + QĐ + DS Excel) */
    async studentPrintExportReturn(request: StudentPrintExportRequest): Promise<Blob> {
        return this.downloadBlob("/api/v1/student-print/export-return", "POST", request);
    }

    /** Xuất hồ sơ Trúng tuyển (ZIP: Thư mời + QĐ + DS Excel) */
    async studentPrintExportAdmission(request: StudentPrintExportRequest): Promise<Blob> {
        return this.downloadBlob("/api/v1/student-print/export-admission", "POST", request);
    }

    /** Chỉ xuất Danh sách Excel */
    async studentPrintExportExcel(type: "dropout" | "deferred" | "return" | "admission", request: StudentPrintExportRequest): Promise<Blob> {
        return this.downloadBlob("/api/v1/student-print/export-excel-only", "POST", { ...request, type });
    }

    /** Xuất từ file Excel danh sách (ZIP hoặc chỉ Excel) */
    async studentPrintExportFromExcel(
        type: "dropout" | "deferred" | "return" | "admission",
        formData: FormData,
        excelOnly: boolean
    ): Promise<Blob> {
        formData.append("type", type);
        formData.append("excelOnly", excelOnly ? "true" : "false");
        return this.downloadBlobForm("/api/v1/student-print/export-from-excel", formData);
    }

    async classPromoteNormalPreview(filter: ClassPromoteNormalFilter): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/class/promote-normal/preview",
            data: filter,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async classPromoteNormalConfirm(classIds: number[]): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/class/promote-normal/confirm",
            data: { classIds },
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    /** Gửi email thông báo tình trạng hồ sơ lưu trữ (CTSV) cho từng sinh viên đã chọn */
    async studentSendDocumentArchiveNotification(studentIds: number[]): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/student/send-document-archive-notification",
            data: { studentIds },
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    /** Import thông tin sinh viên từ Excel (chỉ thông tin, không hồ sơ) */
    async studentBulkImportInfo(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/student/bulk-import-info",
            data: formData,
        });
        return response.data;
    }

    /** Preview đọc và map dữ liệu import thông tin sinh viên trước khi xác nhận */
    async studentBulkImportInfoPreview(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/student/bulk-import-info-preview",
            data: formData,
        });
        return response.data;
    }

    /** Cập nhật địa chỉ sinh viên hàng loạt từ Excel (multipart: file) */
    async studentBulkUpdateAddress(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/student/bulk-update-address",
            data: formData,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptBulkPaymentPreview(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/bulk-payment/preview",
            data: formData,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptBulkPaymentImport(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/bulk-payment/import",
            data: formData,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptCollectionReport(filter: any): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/collection-report",
            data: filter,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptCollectionReportStudents(filter: {
        academicYearId?: number;
        trainingTypeId?: number;
        majorId?: number;
        isPaid: boolean;
        period?: number;
        fromDate?: Date;
        toDate?: Date;
    }): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/collection-report/students",
            data: filter,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async scholarShipBulkImport(formData: FormData): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/scholar-ship/bulk-import",
            data: formData,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    /** Dashboard: mục C + D — thôi học theo thời gian & nguyên nhân */
    async dashboardDropoutOverview(
        academicYearIds?: number[] | null,
        reasonMonthFrom?: string | null,
        reasonMonthTo?: string | null,
    ): Promise<{
        success?: boolean
        data?: DashboardDropoutOverviewDto
    }> {
        const q: string[] = []
        if (academicYearIds?.length) {
            for (const id of academicYearIds) {
                if (id > 0) q.push("academicYearIds=" + encodeURIComponent(String(id)))
            }
        }
        const fromTrimmed = reasonMonthFrom?.trim()
        const toTrimmed = reasonMonthTo?.trim()
        if (fromTrimmed)
            q.push("reasonMonthFrom=" + encodeURIComponent(fromTrimmed))
        if (toTrimmed)
            q.push("reasonMonthTo=" + encodeURIComponent(toTrimmed))
        const qs = q.length ? "?" + q.join("&") : ""
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/dashboard/dropout-overview" + qs,
            headers: { Accept: "application/json" },
        })
        return response.data
    }

    async receiptEmailRemindPreview(body: ReceiptEmailRemindPreviewQueryDto): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/email-remind/preview",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptEmailRemindSend(body: ReceiptEmailRemindSendDto): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/email-remind/send",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptEmailLogList(body: ReceiptEmailLogGetListQuery): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/email-log/list",
            data: body,
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptEmailLogGetById(id: number): Promise<any> {
        const response = await this.instance.request({
            method: "GET",
            url: this.baseUrl + "/api/v1/receipt/email-log/" + id,
            headers: { Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptEmailLogResend(emailLogId: number): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/email-log/resend",
            data: { emailLogId },
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }

    async receiptEmailLogCancel(emailLogId: number): Promise<any> {
        const response = await this.instance.request({
            method: "POST",
            url: this.baseUrl + "/api/v1/receipt/email-log/cancel",
            data: { emailLogId },
            headers: { "Content-Type": "application/json", Accept: "text/plain" },
        });
        return response.data;
    }
}

export interface ReceiptEmailRemindPreviewQueryDto {
    period: number
    academicYearId?: number | null
    trainingType?: number | null
    /** null = tất cả; false = chưa gửi; true = đã gửi */
    emailSent?: boolean | null
    /** Chỉ khi đã gửi: lần gửi gần nhất cách đây tối thiểu N ngày */
    minDaysSinceLastSent?: number | null
}

export interface ReceiptEmailRemindSendDto extends ReceiptEmailRemindPreviewQueryDto {
    receiptIds?: number[]
    sendAllUnpaid?: boolean
}

export interface ReceiptEmailLogGetListQuery {
    pageIndex?: number
    pageSize?: number
    sortExpression?: string
    keyword?: string | null
    period?: number | null
    studentId?: number | null
    status?: number | null
    emailType?: number | null
    sentDateFrom?: string | null
    sentDateTo?: string | null
}
export interface DashboardDropoutOverviewDto {
    currentMonth: DashboardDropoutPeriodBlockDto
    currentQuarter: DashboardDropoutPeriodBlockDto
    currentYear: DashboardDropoutYearBlockDto
    monthlyTrend: DashboardDropoutTrendPointDto[]
    reasons: DashboardDropoutReasonsSectionDto
}

export interface DashboardDropoutPeriodBlockDto {
    periodLabel: string
    dropoutCount: number
    dropoutRatePercent: number
    vsPreviousPeriod?: DashboardDropoutDeltaDto | null
    vsYearAgoPeriod?: DashboardDropoutDeltaDto | null
}

export interface DashboardDropoutYearBlockDto {
    periodLabel: string
    dropoutCountYtd: number
    dropoutRateYtdAverage: number
    vsYtdYearAgo?: DashboardDropoutDeltaDto | null
    vsFullPreviousYear?: DashboardDropoutDeltaDto | null
}

export interface DashboardDropoutDeltaDto {
    label: string
    countDelta: number
    countDeltaPercent: number
    rateDeltaPoints: number
}

export interface DashboardDropoutTrendPointDto {
    year: number
    month: number
    dropoutCount: number
    dropoutRatePercent: number
}

export interface DashboardDropoutReasonsSectionDto {
    topReasons: DashboardDropoutReasonItemDto[]
    summaryNote: string
    windowDropoutStudents: number
}

export interface DashboardDropoutReasonItemDto {
    reasonId: number
    reasonName: string
    factorTag?: string | null
    count: number
    percent: number
}